<template>
  <div class="flex flex-col min-h-0">
    <!-- Content -->
    <div class="flex-1">
      <h3 class="text-lg font-medium leading-6 text-white">List Invitation</h3>
      <p class="mt-2 text-sm text-gray-400">
        You've been invited to join a collaborative list
      </p>

      <!-- List preview -->
      <div v-if="invite" class="mt-4 space-y-3">
        <!-- List Card -->
        <div class="p-4 rounded-lg bg-white/5 border border-white/10">
          <!-- Header with backdrop -->
          <div
            class="relative h-20 -mx-4 -mt-4 mb-3 rounded-t-lg overflow-hidden bg-gradient-to-br from-gray-700 to-gray-800">
            <img
              v-if="invite.list?.backdrop_path"
              :src="`https://image.tmdb.org/t/p/w500${invite.list.backdrop_path}`"
              :alt="invite.list.title"
              class="w-full h-full object-cover opacity-60" />
            <div
              class="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />

            <!-- List icon overlay -->
            <div class="absolute bottom-0 left-4 translate-y-1/2">
              <div
                class="size-12 rounded-xl bg-gray-800 border-2 border-gray-900 flex items-center justify-center shadow-lg">
                <span v-if="invite.list?.icon" class="text-lg">{{
                  invite.list.icon
                }}</span>
                <fa-icon v-else :icon="['fas', 'list']" class="text-white/40" />
              </div>
            </div>
          </div>

          <!-- List Info -->
          <div class="pt-4">
            <p class="text-base font-medium text-white">
              {{ invite.list?.title }}
            </p>
            <p
              v-if="invite.list?.description"
              class="mt-1 text-sm text-gray-400 line-clamp-2">
              {{ invite.list.description }}
            </p>
          </div>
        </div>

        <!-- Inviter -->
        <div class="flex items-center gap-3 p-3 rounded-lg bg-white/5">
          <div
            class="size-10 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
            <img
              v-if="invite.inviter?.avatar_url"
              :src="invite.inviter.avatar_url"
              :alt="invite.inviter.username"
              class="w-full h-full object-cover" />
            <span v-else class="text-sm text-white/60">
              {{ invite.inviter?.username?.charAt(0)?.toUpperCase() }}
            </span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm text-white">
              Invited by
              <span class="font-medium">
                {{ invite.inviter?.displayname || invite.inviter?.username }}
              </span>
            </p>
            <p class="text-xs text-gray-500">
              {{ formatTimeAgo(invite.created_at) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions - full width at bottom -->
    <div class="flex flex-col gap-2 pt-6 mt-auto">
      <button
        type="button"
        :disabled="loading"
        class="py-3 w-full text-sm font-semibold text-white bg-gradient-to-r to-indigo-500 rounded-lg transition-all cursor-pointer from-sky-500 hover:from-sky-400 hover:to-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
        @click="acceptInvite">
        <span v-if="loading" class="flex items-center justify-center gap-2">
          <div
            class="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          Joining...
        </span>
        <span v-else>Accept & Join List</span>
      </button>
      <button
        type="button"
        :disabled="loading"
        class="w-full py-3 text-sm font-medium text-gray-400 rounded-lg cursor-pointer hover:text-white hover:bg-white/10 transition-all disabled:opacity-50"
        @click="declineInvite">
        Decline
      </button>
      <button
        type="button"
        class="w-full py-2 text-xs text-gray-500 rounded-lg cursor-pointer hover:text-gray-400 transition-all"
        @click="close">
        Maybe Later
      </button>
    </div>

    <!-- Error -->
    <div v-if="error" class="mt-4 text-sm text-red-400 text-center">
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStoreLists } from '@/stores/storeLists'
import { formatTimeAgo } from '@/use/useFormatDate.js'

const props = defineProps({
  invite: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'accepted', 'declined'])

const router = useRouter()
const storeLists = useStoreLists()
const loading = ref(false)
const error = ref(null)

const close = () => {
  emit('close')
}

async function acceptInvite() {
  if (loading.value) return
  loading.value = true
  error.value = null

  try {
    await storeLists.acceptUserInvite(props.invite.id)
    emit('accepted')
    emit('close')

    // Navigate to the list
    if (props.invite.list?.id && props.invite.inviter?.username) {
      router.push({
        name: 'WebUserListSingle',
        params: {
          username: props.invite.inviter.username,
          listId: props.invite.list.id
        }
      })
    }
  } catch (err) {
    error.value = err.message || 'Failed to accept invite'
  } finally {
    loading.value = false
  }
}

async function declineInvite() {
  if (loading.value) return
  loading.value = true
  error.value = null

  try {
    await storeLists.declineUserInvite(props.invite.id)
    emit('declined')
    emit('close')
  } catch (err) {
    error.value = err.message || 'Failed to decline invite'
  } finally {
    loading.value = false
  }
}
</script>
